/**
 * Main entry point for React application
 * 
 * Implements Requirements 6.1, 6.2, 11.1, 14.6, 14.8, 17.1, 19.1, 19.2, 19.3, 19.4, 19.5, 19.6, 19.7:
 * - TanStack Router for type-safe routing
 * - Nested routes with layout composition
 * - QueryClient context for data fetching
 * - Toast notifications for transient errors
 * - Error monitoring with Sentry
 * - Analytics platform integration (PostHog)
 * - Page view and navigation tracking
 * - User interaction tracking
 * - i18n initialization for internationalization
 * - Performance monitoring with web vitals
 */
import { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';
import { queryClient } from './lib/queryClient';
import { createAppRouter } from './lib/router';
import { Toaster } from './components/ui/sonner';
import i18n, { loadAllNamespaces } from './i18n/config';
import { initPerformanceMonitoring, sendToAnalytics } from './lib/performance';
import { initSentry } from './lib/sentry';
import { initAnalytics } from './lib/analytics';
import { installGlobalErrorHandlers } from './lib/logger';
import './index.css';

// Initialize Sentry error monitoring (must be first)
initSentry();

// Install structured logging global error handlers
installGlobalErrorHandlers();

// Initialize analytics platform
initAnalytics();

// Initialize performance monitoring
initPerformanceMonitoring(sendToAnalytics);

// Track page load time
import { trackPageLoadTime } from './lib/performance';
import { useTranslation } from 'react-i18next';
trackPageLoadTime();

// Preload all translations for the detected language
loadAllNamespaces(i18n.language);

// Create router instance with QueryClient context
const router = createAppRouter(queryClient);

const root = document.getElementById('root');

if (!root) {
  throw new Error('Root element not found');
}

createRoot(root).render(
  <StrictMode>
    <Suspense fallback={<div>Loading...</div>}>
      <I18nextProvider i18n={i18n}>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
          <Toaster position="top-right" />
        </QueryClientProvider>
      </I18nextProvider>
    </Suspense>
  </StrictMode>
);

// Build timestamp: 2026-03-26 04:03:14
